import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import {
  AppBar,
  Badge,
  Box,
  Button,
  Divider,
  Hidden,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  makeStyles
} from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import NotificationsIcon from '@material-ui/icons/NotificationsOutlined';
import InputIcon from '@material-ui/icons/Input';
import Logo from 'src/components/Logo';
import storage from 'src/storage';
import api from 'src/services/httpService';
import Auth from 'src/api/auth';

const DEFAULT_POLL_INTERVAL_MS = 20000;

const useStyles = makeStyles(theme => ({
  root: {},
  avatar: {
    width: 60,
    height: 60
  },
  menuPaper: {
    width: 360,
    maxHeight: 480
  },
  menuHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 2)
  },
  unreadItem: {
    backgroundColor: theme.palette.action.hover
  },
  emptyState: {
    padding: theme.spacing(3, 2),
    textAlign: 'center'
  },
  itemTime: {
    display: 'block',
    marginTop: theme.spacing(0.5)
  }
}));

const timeAgo = dateString => {
  if (!dateString) return '';
  const seconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateString).toLocaleDateString();
};

const TopBar = ({ className, onMobileNavOpen, ...rest }) => {
  const classes = useStyles();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const intervalRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/notifications');
      setItems(data.data || []);
      setUnreadCount(data.unreadCount || 0);
      return data.pollIntervalMs;
    } catch (error) {
      return undefined;
    }
  }, []);

  // Fetch once, then poll at the interval the API tells us to use (server is
  // the single source of truth for cadence; falls back to 20s).
  useEffect(() => {
    let mounted = true;
    (async () => {
      const interval = await fetchNotifications();
      if (mounted) {
        intervalRef.current = setInterval(
          fetchNotifications,
          interval || DEFAULT_POLL_INTERVAL_MS
        );
      }
    })();
    return () => {
      mounted = false;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchNotifications]);

  const handleOpen = event => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleItemClick = async item => {
    setAnchorEl(null);
    if (!item.read) {
      setItems(prev =>
        prev.map(n => (n.id === item.id ? { ...n, read: true } : n))
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      try {
        await api.patch(`/admin/notifications/${item.id}/read`);
      } catch (error) {
        // best-effort; the next poll reconciles state
      }
    }
    if (item.link) navigate(item.link);
  };

  const handleMarkAllRead = async () => {
    setItems(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await api.patch('/admin/notifications/read-all');
    } catch (error) {
      // best-effort; the next poll reconciles state
    }
  };

  const logout = () => {
    // revoke server-side; don't block navigation on it (ISSUE-0231)
    Auth.logout(storage.getUser()?.refreshToken).catch(() => {});
    storage.removeUser();
    navigate('/login', { replace: true });
  };

  return (
    <AppBar className={clsx(classes.root, className)} elevation={0} {...rest}>
      <Toolbar>
        <RouterLink to="/">
          <Logo />
        </RouterLink>
        <Box flexGrow={1} />
        <IconButton aria-label="Notifications" color="inherit" onClick={handleOpen}>
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleClose}
          classes={{ paper: classes.menuPaper }}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box className={classes.menuHeader}>
            <Typography variant="subtitle1">Notifications</Typography>
            {unreadCount > 0 && (
              <Button size="small" color="primary" onClick={handleMarkAllRead}>
                Mark all read
              </Button>
            )}
          </Box>
          <Divider />
          {items.length === 0 && (
            <Box className={classes.emptyState}>
              <Typography variant="body2" color="textSecondary">
                You&apos;re all caught up
              </Typography>
            </Box>
          )}
          {items.map(item => (
            <MenuItem
              key={item.id}
              onClick={() => handleItemClick(item)}
              className={item.read ? '' : classes.unreadItem}
              style={{ whiteSpace: 'normal' }}
            >
              <ListItemText
                disableTypography
                primary={
                  <Typography
                    variant="body2"
                    component="div"
                    style={{ fontWeight: item.read ? 400 : 600 }}
                  >
                    {item.title}
                  </Typography>
                }
                secondary={
                  <div>
                    <Typography
                      variant="caption"
                      component="div"
                      color="textSecondary"
                    >
                      {item.body}
                    </Typography>
                    <Typography
                      variant="caption"
                      component="div"
                      color="textSecondary"
                      className={classes.itemTime}
                    >
                      {timeAgo(item.createdAt)}
                    </Typography>
                  </div>
                }
              />
            </MenuItem>
          ))}
        </Menu>
        <IconButton aria-label="Log out" onClick={logout} color="inherit">
          <InputIcon />
        </IconButton>
        <Hidden lgUp>
          <IconButton aria-label="Open menu" color="inherit" onClick={onMobileNavOpen}>
            <MenuIcon />
          </IconButton>
        </Hidden>
      </Toolbar>
    </AppBar>
  );
};

TopBar.propTypes = {
  className: PropTypes.string,
  onMobileNavOpen: PropTypes.func
};

export default TopBar;
